import { computed } from "vue";
import { useI18n } from "vue-i18n";
/**
 * Every user-visible string the OPAL `AdvancedFilterBuilder` needs (R29) — OPAL ships no
 * copy. Shared between Contact.vue and OrganisationList.vue (OP-6506 code review) so the
 * `filters.builder.*` label map isn't hand-duplicated per view.
 */
export function useAdvancedFilterLabels(options = {}) {
    const { t, locale } = useI18n();
    return computed(() => {
        // The builder is opened both to create and to edit a saved filter; announcing
        // "Nouveau filtre avancé" while editing one misdescribes what Save will do. In edition
        // the saved filter's own name is interpolated as the `{name}` placeholder of
        // `filters.builder.editTitle` (OP-6885 QA). Blank/whitespace names (unreachable —
        // see the option's doc) collapse to the creation title.
        const editingName = options.editingName?.()?.trim();
        return {
            title: editingName
                ? t("filters.builder.editTitle", { name: editingName })
                : t("filters.builder.title"),
            alert: t("filters.builder.alert"),
            addCriterion: t("filters.builder.addCriterion"),
            addGroup: t("filters.builder.addGroup"),
            and: t("filters.builder.and"),
            or: t("filters.builder.or"),
            fieldPlaceholder: t("filters.builder.fieldPlaceholder"),
            // Reuses the step-1 search placeholder (already "Rechercher un champ…") rather than
            // minting a duplicate key for the same copy in the field picker's own search input.
            fieldSearchPlaceholder: t("filters.searchPlaceholder"),
            fieldSearchClear: t("common.clearSearch"),
            fieldGroupsLabel: t("filters.groupsLabel"),
            fieldNoResults: t("filters.noFieldResults"),
            fieldResultsCount: (count) => t("common.resultsCountPlural", count),
            fieldNoFields: t("filters.noFields"),
            fieldEmptyGroup: t("filters.noRelatedFields"),
            operatorPlaceholder: t("filters.builder.operatorPlaceholder"),
            valuePlaceholder: t("filters.builder.valuePlaceholder"),
            reset: t("filters.builder.reset"),
            cancel: t("filters.builder.cancel"),
            apply: t("filters.builder.apply"),
            save: t("filters.builder.save"),
            // The two reasons Save can be blocked. Without them the button is greyed with no
            // explanation: `isValidLive` gates it without filling `errors`, so neither the inline
            // messages nor the error Alert appear before the first Apply (R69 — never leave an
            // optional OPAL label map partial).
            saveDisabledEmpty: t("filters.builder.saveDisabledEmpty"),
            saveDisabledInvalid: t("filters.builder.saveDisabledInvalid"),
            removeCriterion: t("filters.builder.removeCriterion"),
            operatorLabels: {
                eq: t("filters.builder.operators.eq"),
                neq: t("filters.builder.operators.neq"),
                gt: t("filters.builder.operators.gt"),
                gte: t("filters.builder.operators.gte"),
                lt: t("filters.builder.operators.lt"),
                lte: t("filters.builder.operators.lte"),
                between: t("filters.builder.operators.between"),
                contains: t("filters.builder.operators.contains"),
                notContains: t("filters.builder.operators.notContains"),
                startsWith: t("filters.builder.operators.startsWith"),
                endsWith: t("filters.builder.operators.endsWith"),
                in: t("filters.builder.operators.in"),
                notIn: t("filters.builder.operators.notIn"),
                isNull: t("filters.builder.operators.isNull"),
                isNotNull: t("filters.builder.operators.isNotNull"),
                lastNDays: t("filters.builder.operators.lastNDays"),
                nextNDays: t("filters.builder.operators.nextNDays"),
            },
            errorAlert: t("filters.builder.errorAlert"),
            errorValueRequired: t("filters.builder.errorValueRequired"),
            errorValueInvalid: t("filters.builder.errorValueInvalid"),
            errorRangeInvalid: t("filters.builder.errorRangeInvalid"),
            errorDaysInvalid: t("filters.builder.errorDaysInvalid"),
            errorOperatorIllegal: t("filters.builder.errorOperatorIllegal"),
            errorFieldOrphan: t("filters.builder.errorFieldOrphan"),
            boolTrue: t("filters.builder.boolTrue"),
            boolFalse: t("filters.builder.boolFalse"),
            daysSuffix: t("filters.builder.daysSuffix"),
            rangeFrom: t("filters.builder.rangeFrom"),
            rangeTo: t("filters.builder.rangeTo"),
            removeValue: t("filters.builder.removeValue"),
            valueSearchPlaceholder: t("filters.builder.value.searchPlaceholder"),
            valueNoResults: t("common.noResults"),
            valueConfirm: t("filters.builder.value.confirm"),
            valueSelectAll: t("common.selectAll"),
            valueClearAll: t("filters.builder.value.clearAll"),
            valueRemoveChip: (label) => t("filters.builder.value.removeChip", { label }),
            valueEmpty: t("filters.builder.value.empty"),
            valueLoading: t("common.loading"),
            valueUnknown: t("filters.builder.value.unknown"),
            valueSearchClear: t("common.clearSearch"),
            valueResultsCount: (count) => t("common.resultsCountPlural", count),
            valueMoreResults: (count) => t("filters.builder.value.moreResults", count),
            valueResultsLabel: t("filters.builder.value.results"),
            dateOperatorLabels: {
                eq: t("filters.builder.dateOperators.eq"),
                neq: t("filters.builder.dateOperators.neq"),
                lt: t("filters.builder.dateOperators.lt"),
                gt: t("filters.builder.dateOperators.gt"),
            },
            // String-specific wording (§4 grid): eq = "est exactement", neq = "n'est pas exactement".
            stringOperatorLabels: {
                eq: t("filters.builder.stringOperators.eq"),
                neq: t("filters.builder.stringOperators.neq"),
            },
            // List-type (Enum/Guid) wording (§4 grid): est / n'est pas / est l'une de / n'est aucune de.
            listOperatorLabels: {
                eq: t("filters.builder.listOperators.eq"),
                neq: t("filters.builder.listOperators.neq"),
                in: t("filters.builder.listOperators.in"),
                notIn: t("filters.builder.listOperators.notIn"),
            },
            // Localizes the date-picker calendars (R30); the wire value stays ISO.
            dateLocale: locale.value,
            // Popover-only strings (R69 — never ship a partial OPAL label map).
            viewAllFilters: t("filters.builder.viewAllFilters"),
            confirmCloseTitle: t("filters.builder.confirmClose.title"),
            confirmCloseMessage: t("filters.builder.confirmClose.message"),
            // Reuses the app-wide UnsavedChangesModal "stay" copy (ActionEditorView,
            // WorkspaceSettings, ProfileManagement, …) instead of a duplicate key — same word,
            // same button, same modal shell.
            confirmCloseStay: t("unsavedChanges.stay"),
            confirmCloseLeave: t("filters.builder.confirmClose.leave"),
        };
    });
}
